/**
 * Satzbausteine für die Startseite "Heute". Deterministisch aus /heute
 * (und der Zulaufkarte aus /betrieb/status) gebaut, ohne Sprachmodell und
 * ohne Annahmen: was der Vertrag nicht liefert (Name, Firma, Wunschzeit,
 * Ergebnis eines Gesprächs), kommt hier nicht vor. Alle Zeiten in
 * Europe/Berlin, `now` wird übergeben, damit Tests nicht von der Uhr abhängen.
 */
import type {
  HeuteKennzahl,
  HeuteResponse,
  HeuteRueckrufeFach,
  HeuteZusammenfassungenFach,
  LeitstandStoerung,
  RueckrufListItem,
  ZulaufStatusKarte,
} from "../../api/types";
import { abteilungLabel } from "../../lib/labels";

const TZ = "Europe/Berlin";

/** Ein Claim ab diesem Alter gilt als verwaist (VERTRAG.md, Claim-Regeln). */
export const CLAIM_VERWAIST_MS = 30 * 60_000;

const hhmm = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
const dayKey = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: TZ });
const dayMonth = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", timeZone: TZ });
const weekday = new Intl.DateTimeFormat("de-DE", { weekday: "long", timeZone: TZ });
const longDate = new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric", timeZone: TZ });
const hourOnly = new Intl.DateTimeFormat("de-DE", { hour: "numeric", hourCycle: "h23", timeZone: TZ });

/* ------------------------------------------------------------ Zahlen */

const WORT = ["null", "eins", "zwei", "drei", "vier", "fünf", "sechs", "sieben", "acht", "neun", "zehn", "elf", "zwölf"];

/** Zahl als Wort bis zwölf, darüber Ziffern; `eins` je nach Genus. */
export function zahlwort(n: number, genus: "m" | "f" | "n" = "n"): string {
  if (n === 1) return genus === "f" ? "eine" : "ein";
  return n >= 0 && n < WORT.length ? WORT[n] : String(n);
}

function gross(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/* ------------------------------------------------------------- Zeiten */

/** "12:13" in Berliner Zeit. */
export function uhrzeit(iso: string): string {
  return hhmm.format(new Date(iso));
}

/** "um 12:13", "gestern um 18:40" oder "am 08.10. um 09:00". */
export function zeitpunkt(iso: string, now: Date): string {
  const date = new Date(iso);
  const tage = Math.round(
    (Date.parse(dayKey.format(now)) - Date.parse(dayKey.format(date))) / 86_400_000,
  );
  if (tage === 0) return `um ${hhmm.format(date)}`;
  if (tage === 1) return `gestern um ${hhmm.format(date)}`;
  return `am ${dayMonth.format(date)} um ${hhmm.format(date)}`;
}

/** Wartedauer kurz: "47 min", "2 h 11 min", "3 Tage". */
export function dauer(iso: string, now: Date): string {
  const minuten = Math.max(0, Math.floor((now.getTime() - Date.parse(iso)) / 60_000));
  if (minuten < 60) return `${minuten} min`;
  if (minuten < 24 * 60) {
    const rest = minuten % 60;
    return rest === 0 ? `${Math.floor(minuten / 60)} h` : `${Math.floor(minuten / 60)} h ${rest} min`;
  }
  const tage = Math.floor(minuten / (24 * 60));
  return tage === 1 ? "1 Tag" : `${tage} Tage`;
}

/** ", seit über zwei Stunden" ab einer Stunde Wartezeit, sonst leer. */
function seitUeber(iso: string, now: Date): string {
  const stunden = Math.floor((now.getTime() - Date.parse(iso)) / 3_600_000);
  if (stunden < 1) return "";
  if (stunden < 24) return stunden === 1 ? "seit über einer Stunde" : `seit über ${zahlwort(stunden)} Stunden`;
  const tage = Math.floor(stunden / 24);
  return tage === 1 ? "seit über einem Tag" : `seit über ${zahlwort(tage)} Tagen`;
}

/** Marginalie oben: Wochentag und Datum mit Uhrzeit. */
export function datumszeile(now: Date): { wochentag: string; datum: string } {
  return { wochentag: weekday.format(now), datum: `${longDate.format(now)}, ${hhmm.format(now)}` };
}

export function gruss(now: Date): string {
  const stunde = Number(hourOnly.formatToParts(now).find((part) => part.type === "hour")?.value);
  if (stunde < 11) return "Guten Morgen.";
  if (stunde < 18) return "Guten Tag.";
  return "Guten Abend.";
}

/* ----------------------------------------------------------- Rückrufe */

/** Ältester Eintrag nach Eingang, unabhängig von der Serverreihenfolge. */
export function aeltester(items: RueckrufListItem[]): RueckrufListItem | null {
  return items.reduce<RueckrufListItem | null>(
    (best, item) => (best === null || Date.parse(item.eingegangen_am) < Date.parse(best.eingegangen_am) ? item : best),
    null,
  );
}

/** Wer gerade dran ist; ein Claim ab 30 Minuten gilt als verwaist und damit frei. */
export function bearbeiter(item: RueckrufListItem, now: Date): { name: string; seit: string } | null {
  if (!item.claimed_by || !item.claimed_at) return null;
  if (now.getTime() - Date.parse(item.claimed_at) >= CLAIM_VERWAIST_MS) return null;
  return { name: item.claimed_by, seit: item.claimed_at };
}

function wert(kennzahl: HeuteKennzahl, gesamt: number | null): number | null {
  return kennzahl.wert ?? gesamt;
}

function satzGespraeche(n: number | null): string {
  if (n === null) return "Wie viele Zusammenfassungen seit gestern eingegangen sind, ist nicht gemessen.";
  if (n === 0) return "Seit gestern ist noch keine Zusammenfassung eingegangen.";
  if (n === 1) return "Seit gestern ist eine Zusammenfassung eingegangen.";
  return `Seit gestern sind ${zahlwort(n, "f")} Zusammenfassungen eingegangen.`;
}

function satzRueckrufe(fach: HeuteRueckrufeFach, n: number | null, now: Date): string {
  if (n === null) return "Wie viele Rückrufe fällig sind, ist nicht gemessen.";
  if (n === 0) return "Derzeit ist kein Rückruf fällig.";
  const alt = aeltester(fach.eintraege);
  const lange = alt ? seitUeber(alt.eingegangen_am, now) : "";
  if (n === 1) return `Ein Rückruf ist fällig${lange ? `, eingegangen ${lange.replace("seit über", "vor über")}` : ""}.`;
  return `${gross(zahlwort(n, "m"))} Rückrufe sind fällig${lange ? `, der älteste eingegangen ${lange.replace("seit über", "vor über")}` : ""}.`;
}

function satzStoerungen(n: number | null): string {
  if (n === null) return "Ob es Störungen gibt, ist nicht gemessen.";
  if (n === 0) return "Keine Störung gemeldet.";
  if (n === 1) return "Eine Störung ist gemeldet.";
  return `${gross(zahlwort(n, "f"))} Störungen sind gemeldet.`;
}

/**
 * Kopfsatz der Startseite. Ein Fach ohne Recht (`verfuegbar: false`)
 * kommt nicht vor, auch wenn seine Kopfzahl einen Wert trägt.
 */
export function kopfsatz(heute: HeuteResponse, now: Date): string {
  const teile = [gruss(now)];
  const z = heute.neue_zusammenfassungen;
  if (z.verfuegbar) teile.push(satzGespraeche(wert(heute.kopfzahlen.zusammenfassungen_seit_tagesgrenze, z.gesamt)));
  const r = heute.rueckrufe_offen;
  if (r.verfuegbar) teile.push(satzRueckrufe(r, wert(heute.kopfzahlen.rueckrufe_offen, r.gesamt), now));
  const s = heute.stoerungen;
  if (s.verfuegbar) teile.push(satzStoerungen(wert(heute.kopfzahlen.stoerungen, s.gesamt)));
  if (teile.length === 1) teile.push("Für Ihre Rolle gibt es hier heute nichts zu sehen.");
  return teile.join(" ");
}

function letzterImport(zulauf: ZulaufStatusKarte | null | undefined, now: Date): string {
  const at = zulauf?.letzter_erfolgreicher_import_at;
  return at ? ` Letzter Import ${zeitpunkt(at, now)}.` : "";
}

/** Absatz im Abschnitt Rückrufe. */
export function rueckrufeAbsatz(
  fach: HeuteRueckrufeFach,
  now: Date,
  zulauf?: ZulaufStatusKarte | null,
): string {
  const gesamt = fach.gesamt ?? 0;
  const items = fach.eintraege;
  if (gesamt === 0 || items.length === 0) return `Kein Rückruf fällig.${letzterImport(zulauf, now)}`;
  const alt = aeltester(items)!;
  const abteilung = alt.abteilung ? `für ${abteilungLabel(alt.abteilung)}` : "ohne Abteilung";
  const saetze = [`Der älteste fällige Rückruf ist ${abteilung}, eingegangen ${zeitpunkt(alt.eingegangen_am, now)}.`];
  const frei = items.filter((item) => bearbeiter(item, now) === null).length;
  const k = items.length;
  const gekuerzt = gesamt > k;
  if (frei === 0) {
    saetze.push(k === 1 ? "Jemand ist schon dran." : gekuerzt ? `Bei den ${zahlwort(k)} ältesten ist schon jemand dran.` : "Bei allen ist schon jemand dran.");
  } else if (frei === k) {
    saetze.push(k === 1 ? "Noch ist niemand dran." : gekuerzt ? `Bei den ${zahlwort(k)} ältesten ist noch niemand dran.` : "Bei allen ist noch niemand dran.");
  } else {
    saetze.push(`Bei ${frei === 1 ? "einem" : zahlwort(frei)} ist noch niemand dran.`);
  }
  if (gekuerzt) saetze.push(`Unten stehen die ${zahlwort(k)} ältesten von ${gesamt}.`);
  return saetze.join(" ");
}

/** Absatz im Abschnitt Gespräche. */
export function gespraecheAbsatz(
  fach: HeuteZusammenfassungenFach,
  now: Date,
  zulauf?: ZulaufStatusKarte | null,
): string {
  const gesamt = fach.gesamt ?? 0;
  if (gesamt === 0 || fach.eintraege.length === 0) {
    return `Seit gestern 0:00 ist keine Zusammenfassung eingegangen.${letzterImport(zulauf, now)}`;
  }
  const juengste = fach.eintraege.reduce((a, b) => (Date.parse(b.importiert_am) > Date.parse(a.importiert_am) ? b : a));
  return `Die jüngste Zusammenfassung kam ${zeitpunkt(juengste.importiert_am, now)}.`;
}

/** Ein Satz zum Zulauf aus der Betriebskarte. */
export function zulaufSatz(zulauf: ZulaufStatusKarte | null | undefined, now: Date): string {
  if (!zulauf) return "";
  const ok = zulauf.letzter_erfolgreicher_import_at;
  if (zulauf.feed_state === "live") {
    return ok ? `Der Anrufimport läuft, der letzte Import war ${zeitpunkt(ok, now)}.` : "Der Anrufimport läuft.";
  }
  if (zulauf.feed_state === "stale") {
    return ok
      ? `Der Anrufimport verzögert, der letzte erfolgreiche Import war ${zeitpunkt(ok, now)}.`
      : "Der Anrufimport verzögert, bisher gab es keinen erfolgreichen Import.";
  }
  return "Zum Anrufimport liegt noch kein Importlauf vor.";
}

/** Satz zu Störungen für den Abschnitt Betrieb; `stoerung` färbt ihn rot. */
export function betriebSatz(heute: HeuteResponse): { text: string; stoerung: boolean } {
  const n = wert(heute.kopfzahlen.stoerungen, heute.stoerungen.gesamt);
  if (n === null) {
    const grund = heute.kopfzahlen.stoerungen.grund ?? heute.stoerungen.grund;
    return { text: `Störungen sind nicht gemessen${grund ? ` (${grund})` : ""}.`, stoerung: false };
  }
  return { text: satzStoerungen(n), stoerung: n > 0 };
}

/** Zeile einer Störung: "um 12:10, Import: Beschreibung". */
export function stoerungZeile(s: LeitstandStoerung, now: Date): string {
  return `${gross(zeitpunkt(s.zeitpunkt, now))}: ${s.beschreibung}`;
}
